import {useCallback, useEffect, useRef, useState} from "react";
import moment from "moment";
import {PROJECT_RESPONSE_TIME_TYPES, ProjectStatisticRepository} from "../../../repositories/ProjectStatisticRepository";
import {DashboardRepository, STATISTIC_PERIODS} from "../../../repositories/DashboardRepository";
import {useStompWebsocket} from "../../../hooks/useStompWebsocket";

// Active calls of one language are read from its flow's statistic; with no language, from the project statistic
// (project-wide snapshots, other filters ignored)
export const ACTIVE_CALLS = "ACTIVE_CALLS";
const CHART_TYPES = [ACTIVE_CALLS, ...PROJECT_RESPONSE_TIME_TYPES];

// Charts start on one-hour buckets (the last 24 hours)
export const DEFAULT_PROJECT_RESPONSE_TIME_PERIOD = "HOURLY";

const DATE_TIME = "YYYY-MM-DDTHH:mm:ss";

const isCancel = (error) => error?.name === "CanceledError" || error?.name === "AbortError";

export const findPeriod = (period) =>
  STATISTIC_PERIODS.find(p => p.value === period) || STATISTIC_PERIODS[0];

// Turns a card's stored window ({period, end}) into the period and start/end to request
export const resolveResponseTimeWindow = (window) => {
  const period = window?.period || DEFAULT_PROJECT_RESPONSE_TIME_PERIOD;
  const {window: size} = findPeriod(period);
  const end = window?.end ? window.end.clone() : moment();
  return {period, start: end.clone().subtract(size.amount, size.unit), end};
};

// Loads the project statistics of the dashboard and refetches them every refreshInterval ms (null = off).
// filters: {subProjectId, projectVersionType, segment, conversationType}; flowUid: the filtered language's flow,
// for the active-calls chart (null = every language)
export const useProjectStatistics = ({projectId, flowUid, filters, refreshInterval}) => {
  // {[type]: ProjectResponseTimeDto}, ACTIVE_CALLS: peak concurrent calls per bucket
  const [responseTime, setResponseTime] = useState({});
  const [responseTimeLoading, setResponseTimeLoading] = useState({});
  // Each response-time card has its own window: {[type]: {period, end}}, end is a moment or null while live.
  // Kept in a ref so changing one card refetches only that card.
  const windowsRef = useRef({});
  const [responseTimeWindows, setResponseTimeWindows] = useState({});
  // Latest request per card, so a slower older response can't overwrite a newer one
  const requestsRef = useRef({});

  const [activeCalls, setActiveCalls] = useState(null);
  // ProjectActiveCallsBreakdownDto of the current filters: active calls per sub project and version type
  const [activeCallsBreakdown, setActiveCallsBreakdown] = useState(null);

  // A message is pushed whenever a conversation is created or changes status: for the filtered language when there
  // is one, else for the whole project. It only triggers a reload of the active-calls breakdown (which applies every
  // filter); the active-calls chart keeps loading on the refresh interval.
  const activeTopic = filters?.subProjectId
    ? `/topic/sub-project/${filters.subProjectId}/active-conversations`
    : projectId ? `/topic/project/${projectId}/active-conversations` : null;
  const [activeUpdate] = useStompWebsocket(activeTopic);
  useEffect(() => {
    if (!activeUpdate) return;
    // changes of another conversation type than the one shown are ignored
    const type = filters?.conversationType;
    if (type && activeUpdate.conversationType && activeUpdate.conversationType !== type) return;
    loadActiveCallsRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUpdate]);
  const [lastFetched, setLastFetched] = useState(null);

  const filtersKey = JSON.stringify(filters || {});
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const loadResponseTime = useCallback(async (type, signal) => {
    if (!projectId) return;

    const requestId = (requestsRef.current[type] || 0) + 1;
    requestsRef.current[type] = requestId;
    const isLatest = () => requestsRef.current[type] === requestId;

    const {period, start, end} = resolveResponseTimeWindow(windowsRef.current[type]);
    setResponseTimeLoading(prev => ({...prev, [type]: true}));
    try {
      const range = {type, period, start: start.format(DATE_TIME), end: end.format(DATE_TIME), signal};
      const response = type === ACTIVE_CALLS && flowUid
        ? await DashboardRepository.getResponseTime({flowUid, ...range})
        : await ProjectStatisticRepository.getResponseTime({
          projectId, filters: type === ACTIVE_CALLS ? {} : filtersRef.current, ...range,
        });
      if (isLatest()) setResponseTime(prev => ({...prev, [type]: response?.data || null}));
    } catch (error) {
      if (!isCancel(error)) console.error(`Error fetching ${type} response time:`, error);
    } finally {
      if (isLatest()) setResponseTimeLoading(prev => ({...prev, [type]: false}));
    }
    // filtersKey: refetch when the filter values change, not their object identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, flowUid, filtersKey]);

  const loadActiveCallsRef = useRef(null);
  // Latest active-calls request; websocket pushes can start several at once and a slower older one must not win
  const activeCallsRequestRef = useRef(0);
  const loadActiveCalls = useCallback(async (signal) => {
    if (!projectId) return;
    const requestId = ++activeCallsRequestRef.current;
    try {
      const response = await ProjectStatisticRepository.getActiveCallsBreakdown({
        projectId, filters: filtersRef.current, signal
      });
      if (activeCallsRequestRef.current !== requestId) return;
      setActiveCallsBreakdown(response?.data || null);
      setActiveCalls(Number(response?.data?.total) || 0);
    } catch (error) {
      if (!isCancel(error)) console.error("Error fetching active calls:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, filtersKey]);

  loadActiveCallsRef.current = loadActiveCalls;

  const refreshAll = useCallback(async (signal) => {
    await Promise.all([
      ...CHART_TYPES.map(type => loadResponseTime(type, signal)),
      loadActiveCalls(signal),
    ]);
    if (!signal?.aborted) setLastFetched(new Date());
  }, [loadResponseTime, loadActiveCalls]);

  // Initial load, and again when the project or filters change
  useEffect(() => {
    const controller = new AbortController();
    refreshAll(controller.signal);
    return () => controller.abort();
  }, [refreshAll]);

  // Auto refresh; live windows move along with now, shifted ones refetch the same range
  useEffect(() => {
    if (!refreshInterval) return;
    let controller = null;
    const intervalId = setInterval(() => {
      controller?.abort();
      controller = new AbortController();
      refreshAll(controller.signal);
    }, refreshInterval);
    return () => {
      clearInterval(intervalId);
      controller?.abort();
    };
  }, [refreshInterval, refreshAll]);

  const updateWindow = useCallback((type, changes) => {
    windowsRef.current = {...windowsRef.current, [type]: {...windowsRef.current[type], ...changes}};
    setResponseTimeWindows(windowsRef.current);
    return loadResponseTime(type);
  }, [loadResponseTime]);

  // Picking a period jumps back to the live window (ending now)
  const setResponseTimePeriod = useCallback((type, period) =>
    updateWindow(type, {period, end: null}), [updateWindow]);

  // direction: -1 moves the window back, 1 forward, by half a window; never past now
  const shiftResponseTime = useCallback((type, direction) => {
    const {period, end} = resolveResponseTimeWindow(windowsRef.current[type]);
    const {step} = findPeriod(period);
    const nextEnd = direction < 0
      ? end.clone().subtract(step.amount, step.unit)
      : end.clone().add(step.amount, step.unit);
    return updateWindow(type, {end: nextEnd.isBefore(moment()) ? nextEnd : null});
  }, [updateWindow]);

  return {
    responseTime,
    responseTimeLoading,
    responseTimeWindows,
    setResponseTimePeriod,
    shiftResponseTime,
    activeCalls,
    activeCallsBreakdown,
    lastFetched,
    refreshAll,
  };
};
